<template>
  <div v-if="journal" :class="$style.journal_show_content">
    <div :class="$style.journal_section">
      <div v-if="journal.thumbnail_image" :class="$style.thumbnail">
        <AtomsConsumerImage :src="journal.thumbnail_image" :alt="journal.title" :width="640" :height="360" />
      </div>
      <h1 :class="$style.title">{{ journal.title }}</h1>
      <div :class="$style.descriptions">
        <span :class="$style.date">{{ formatDate(journal.release_date) }}</span>
        <div v-if="journal.product_category" :class="$style.tags">
          <AtomsConsumerLink
            v-if="journal.product_category"
            :class="$style.link"
            :url="{ name: 'journals-search', query: { searchQuery: journal.product_category.name } }"
          >
            #{{ journal.product_category.name }}
          </AtomsConsumerLink>
          <AtomsConsumerLink
            v-if="journal.product_sub_category"
            :class="$style.link"
            :url="{ name: 'journals-search', query: { searchQuery: journal.product_sub_category.name } }"
          >
            #{{ journal.product_sub_category.name }}
          </AtomsConsumerLink>
        </div>
      </div>
      <div :class="$style.main_contents" v-html="sanitizeHtmlContent(journal.lead)" />

      <div v-if="headings && headings.length > 0" :class="$style.toc">
        <div :class="$style.toc_inner">
          <p :class="$style.toc_title">目次</p>
          <ul :class="$style.toc_lists">
            <li v-for="(heading, index) in headings" :key="index" :class="$style.toc_lists_item">
              <span :class="$style.num">{{ `0${index + 1}` }}</span>
              <AtomsConsumerLink v-scroll-to="`#heading${index + 1}`" :class="$style.link">{{
                heading
              }}</AtomsConsumerLink>
            </li>
          </ul>
        </div>
      </div>

      <div
        v-if="journal.product_category && journal.supervisor && (journal.supervisor.description ?? '').length > 0"
        :class="$style.supervisor_container"
      >
        <div :class="$style.profile">
          <div v-if="journal.supervisor.icon_image">
            <div :class="$style.img_box">
              <AtomsConsumerImage
                :src="journal.supervisor.icon_image"
                alt="イメージ"
                :class="$style.journal_img"
                :width="60"
                :height="60"
              />
            </div>
          </div>
          <div :class="$style.profile_txt">
            <p :class="$style.author_name">監修者：{{ journal.supervisor.name }}</p>
          </div>
        </div>
        <AtomsConsumerButton :class="$style.button" @click="clickButton()">
          {{ buttonText }}
        </AtomsConsumerButton>
        <div
          :class="[$style.description, $style.html, { [$style.isOmit]: state.omitDescription }]"
          v-html="sanitizeHtmlContent(journal.supervisor.description)"
        />
      </div>
    </div>
    <div :class="$style.journal_content">
      <div :class="$style.html" v-html="sanitizeHtmlContent(journal.content)" />
    </div>

    <div v-if="commonPartContent" :class="$style.article_common_part">
      <div :class="$style.html" v-html="sanitizeHtmlContent(commonPartContent)" />
    </div>
    <OrganismsShare v-bind="shareInfo" />
  </div>
</template>

<script setup lang="ts">
import { formatDate } from "@tential/ec-gql-schema/utils/functions/date"
import { sanitizeHtmlContent } from "@tential/ec-gql-schema/utils/functions/sanitize"
import type { JournalListItemType } from "~/types/journal"

const props = defineProps({
  journal: {
    type: Object as PropType<JournalListItemType>,
    required: true,
  },
  meta: {
    type: Object as PropType<Object | undefined>,
    required: false,
    default: undefined,
  },
  commonPartContent: {
    type: String,
    required: false,
    default: "",
  },
  commonPartCss: {
    type: String,
    required: false,
    default: "",
  },
  headings: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => [],
  },
})
type State = {
  omitDescription: boolean
}
const state: State = reactive({
  omitDescription: true,
})
const route = useRoute()
const shareInfo = computed(() => ({
  title: props.journal.title,
  url: useRuntimeConfig().public.BASE_URL + route.fullPath,
}))
const buttonText = computed(() => {
  return state.omitDescription ? "プロフィールをもっと見る" : "プロフィールを閉じる"
})
const clickButton = (): void => {
  state.omitDescription = !state.omitDescription
}
</script>

<style module lang="scss">
.journal_show_content {
  width: 95vw;
  margin: auto;
  padding-top: 2rem;
  @include md {
    max-width: $max-width-md;
    padding-top: 4rem;
  }
  .thumbnail {
    aspect-ratio: 16 / 9;
    width: 100%;
    margin-bottom: 2rem;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 0.5rem;
    }
  }
  .title {
    font-size: $font-size-20;
    color: $primary-100;
    margin-bottom: 26px;
    line-height: 1.55;
    @include md {
      font-size: $font-size-26;
      margin-bottom: 30px;
    }
  }
  .descriptions {
    display: flex;
    gap: 1rem;
    padding: 1.25rem 0;
    margin-bottom: 30px;
    color: $primary-100;
    border-top: 1px solid $primary-20;
    border-bottom: 1px solid $primary-20;
    @include md {
      font-size: $font-size-14;
      line-height: 1.07;
      padding: 22px 0;
      margin-bottom: 50px;
    }
    .tags {
      display: flex;
      gap: 0.25rem;
      @include md {
        gap: 0.5rem;
      }
      .link {
        color: $primary;
        text-decoration: underline;
      }
    }
  }
  // 目次
  .toc {
    margin-top: 1.25rem;
    .toc_inner {
      width: 400px;
      max-width: 95vw;
      padding: 1rem;
      margin: 2rem auto;
      background: $primary-10;
      border: 1px solid $primary-20;
      @include md {
        padding: 1.5rem;
      }
      .toc_title {
        margin-bottom: 0.7rem;
        font-size: 1rem;
        font-weight: bold;
        text-align: center;
      }
      .toc_lists {
        padding: 0 0.5rem 0;
        & > :not(:first-child) {
          margin-top: 0.7rem;
        }
        .toc_lists_item {
          display: flex;
          .num {
            font-family: $main-font;
            padding-right: 0.2rem;
            font-weight: bold;
            color: $primary;
            text-decoration: none;
            margin-right: 0.5rem;
          }
          .link {
            position: relative;
            display: table-cell;
            cursor: pointer;
            text-decoration: underline;
          }
        }
      }
    }
  }
  .supervisor_container {
    background-color: $primary-10;
    border: 1px solid $primary-20;
    padding: 2rem;
    .profile {
      display: flex;
      align-items: center;
      margin-bottom: 0.5rem;

      .profile_txt {
        margin-left: 1rem;
        p {
          color: $primary;
        }
      }

      .img_box {
        display: inline-block;
        width: 3.3rem;
        height: 3.3rem;

        @include md {
          width: 4.47rem;
          height: 4.47rem;
        }

        img {
          width: 100%;
          height: 100%;
          background-position: center center;
          background-size: cover;
          border: 2px solid $primary;
          border-radius: 50%;
        }
      }
    }
    button {
      text-decoration: underline;
    }
    .description {
      display: block;
      color: $text-black;
      opacity: 1;
      transition: all 0.2s ease-in-out;
      &.isOmit {
        max-height: 0rem;
        line-height: 0;
        color: transparent;
        opacity: 0;
      }
    }
  }
  .journal_content,
  .article_common_part {
    margin-bottom: 3rem;
  }
  .html {
    :global {
      h2 {
        padding-bottom: 0.25rem;
        margin-top: 3.125rem;
        margin-bottom: 1.5rem;
        font-size: $font-size-22;
        color: $primary;
        @include md {
          font-size: $font-size-26;
        }
      }

      h3 {
        padding-left: 0.75rem;
        margin-bottom: 1.125rem;
        border-left: 0.25rem solid $primary;
      }
      em {
        font-style: initial;
        font-weight: bold;
        background: linear-gradient(transparent 65%, $highlighter-blue 65%);
      }
      u {
        font-weight: bold;
        text-decoration: none;
        background: linear-gradient(transparent 65%, $highlighter-pink 65%);
      }
      ul,
      ol {
        padding: 0 1.5rem 0;
      }
      a {
        display: inline;
        color: $primary;
        text-decoration: underline;
      }
      img {
        width: 100%;
        height: auto;
      }
    }
  }
}
</style>
